import { client } from "../utils/axios";
import {
  buildQueryParams as pageBuildQueryParams,
  isPage,
  Page,
  parseQueryParams as pageParseQueryParams,
  QueryParams as PageQueryParams,
} from "./page";
import { isObject, typeFormatFromArray } from "./utils";

export type SignupParamItem = {
  uid: string;
  email?: string;
  params: string;
  lastName?: string;
  firstName?: string;
  nickname?: string;
  birthYear?: number;
  prefecture?: string;
  gender?: string;
  createdAt: Date;
};

function isSignupParamItem(x: unknown): x is SignupParamItem {
  return (
    isObject(x) &&
    typeof (x as SignupParamItem).uid === "string" &&
    typeof (x as SignupParamItem).params === "string" &&
    (x as SignupParamItem).createdAt instanceof Date
  );
}

function formatSignupParamItem(x: unknown): SignupParamItem | null {
  if (!isObject(x)) {
    return null;
  }
  const raw = x as Record<string, unknown>;
  if (typeof raw.createdAt !== "string") {
    return null;
  }
  const res = {
    ...raw,
    createdAt: new Date(raw.createdAt as string),
  };
  if (!isSignupParamItem(res)) {
    return null;
  }
  return res;
}

export type QueryParams = {
  name?: string;
  params?: string;
  excludeNoProfile?: boolean;
  hasSignupParamOnly?: boolean;
} & PageQueryParams;

const NAME = "name";
const PARAMS = "params";
const EXCLUDE_NO_PROFILE = "excludeNoProfile";
const HAS_SIGNUP_PARAM_ONLY = "hasSignupParamOnly";

export function parseQueryParams(search: string): QueryParams {
  const urlParams = new URLSearchParams(search);
  const result: QueryParams = {};

  const name = urlParams.get(NAME);
  if (name) result[NAME] = name;

  const params = urlParams.get(PARAMS);
  if (params) result[PARAMS] = params;

  const excludeNoProfile = urlParams.get(EXCLUDE_NO_PROFILE);
  if (excludeNoProfile === "true") result[EXCLUDE_NO_PROFILE] = true;

  const hasSignupParamOnly = urlParams.get(HAS_SIGNUP_PARAM_ONLY);
  if (hasSignupParamOnly === "true") result[HAS_SIGNUP_PARAM_ONLY] = true;

  const page = pageParseQueryParams(search);

  return { ...result, ...page };
}

export function buildQueryParams(params: QueryParams): URLSearchParams {
  const init: string[][] = [];

  const p = new URLSearchParams();
  const { name, params: paramsFilter, excludeNoProfile, hasSignupParamOnly } = params;
  if (name) {
    p.set(NAME, name);
  }
  if (paramsFilter) {
    p.set(PARAMS, paramsFilter);
  }
  if (excludeNoProfile) {
    p.set(EXCLUDE_NO_PROFILE, "true");
  }
  if (hasSignupParamOnly) {
    p.set(HAS_SIGNUP_PARAM_ONLY, "true");
  }
  init.push(...Array.from(p));

  const pp = pageBuildQueryParams(params);
  init.push(...Array.from(pp));

  return new URLSearchParams(init);
}

export async function getSignupParams(
  params?: QueryParams
): Promise<{ signupParams: SignupParamItem[]; page: Page; totalCount: number }> {
  const c = await client();
  const res = await c.get("/signupParams", { params });
  const { signupParams: resItems, page: resPage, totalCount } = res.data;
  const signupParams = typeFormatFromArray(resItems, formatSignupParamItem);
  if (signupParams && isPage(resPage)) {
    return { signupParams, page: resPage, totalCount: totalCount ?? 0 };
  }
  throw Error("failed to get signup params");
}
